/* Go-Tone Pro buy box: gallery slider custom element (assets/gtp-gallery.js)
 * Vanilla ES2018, no external libraries. See dev/gtp-buy-box/README.md for
 * the gallery/buy-box event contract this file implements.
 *
 * Listens on document for 'gtp:tier-change' (dispatched by
 * assets/gtp-buy-box.js) and jumps to the matching slide:
 *   - event.detail.tierBlockId matches a slide's data-gtp-tier-block -> that slide
 *   - else event.detail.imageIndex is a number >= 0 -> that slide index
 *   - otherwise: no-op
 */
if (!customElements.get('gtp-gallery')) {
  class GtpGallery extends HTMLElement {
    constructor() {
      super();
      this._onTrackScroll = this._onTrackScroll.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onTierChange = this._onTierChange.bind(this);
      this.currentIndex = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-gtp-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-gtp-slide]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('.gtp-gallery__thumb'));
      this.prevBtn = this.querySelector('[data-gtp-prev]');
      this.nextBtn = this.querySelector('[data-gtp-next]');
      this.playBtn = this.querySelector('[data-gtp-play]');
      this.counterCurrent = this.querySelector('[data-gtp-counter-current]');

      this.reducedMotion = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

      if (!this.track || this.slides.length === 0) {
        return;
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', this.prev.bind(this));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', this.next.bind(this));
      }
      if (this.playBtn) {
        this.playBtn.addEventListener('click', this._onPlayClick.bind(this));
      }

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          var index = parseInt(thumb.getAttribute('data-index'), 10);
          if (!isNaN(index)) {
            this.goTo(index);
          }
        });
      });

      this.addEventListener('keydown', this._onKeydown);
      document.addEventListener('gtp:tier-change', this._onTierChange);

      this._setupObserver();
      this.imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this._loadSlideImage(this.slides.indexOf(entry.target));
        });
      }, { root: this.track, rootMargin: '0px 100%', threshold: 0.01 });
      this.slides.forEach((slide) => this.imageObserver.observe(slide));
      this._setActiveIndex(0, { scroll: false });
    }

    disconnectedCallback() {
      clearTimeout(this._scrollTimer);
      document.removeEventListener('gtp:tier-change', this._onTierChange);
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.imageObserver) this.imageObserver.disconnect();
      if (this.track) {
        this.track.removeEventListener('scroll', this._onTrackScroll);
      }
    }

    _setupObserver() {
      this.track.addEventListener('scroll', this._onTrackScroll, { passive: true });
    }

    _onTrackScroll() {
      if (this._scrollTimer) {
        clearTimeout(this._scrollTimer);
      }
      this._scrollTimer = setTimeout(() => {
        var trackRect = this.track.getBoundingClientRect();
        var center = trackRect.left + trackRect.width / 2;
        var closestIndex = this.currentIndex;
        var closestDistance = Infinity;
        this.slides.forEach((slide, index) => {
          var rect = slide.getBoundingClientRect();
          var slideCenter = rect.left + rect.width / 2;
          var distance = Math.abs(slideCenter - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        if (closestIndex !== this.currentIndex) {
          this._setActiveIndex(closestIndex, { scroll: false });
        }
      }, 160);
    }

    _onKeydown(event) {
      if (event.target.closest('video, input, select, textarea')) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      }
    }

    _onPlayClick() {
      var indexAttr = this.playBtn.getAttribute('data-video-index');
      var index = parseInt(indexAttr, 10);
      if (isNaN(index)) {
        return;
      }
      this.goTo(index);
      var slide = this.slides[index];
      if (slide) {
        var video = slide.querySelector('video');
        if (video && typeof video.play === 'function') {
          var playback = video.play();
          if (playback) playback.catch(function () {});
        }
      }
    }

    _onTierChange(event) {
      var detail = event.detail || {};

      if (detail.tierBlockId) {
        var matchIndex = -1;
        this.slides.forEach((slide, index) => {
          if (slide.getAttribute('data-gtp-tier-block') === detail.tierBlockId) {
            matchIndex = index;
          }
        });
        if (matchIndex !== -1) {
          this.goTo(matchIndex);
          return;
        }
      }

      if (this.hasAttribute('data-curated-gallery')) {
        if (detail.userInitiated) {
          var tierSlide = this.querySelector('[data-gtp-tier-block="' + detail.blockId + '"]');
          if (tierSlide) {
            this.goTo(this.slides.indexOf(tierSlide));
            if (window.matchMedia('(max-width: 749px)').matches) {
              this.querySelector('.gtp-gallery__viewer').scrollIntoView({
                behavior: this.reducedMotion ? 'instant' : 'smooth', block: 'start', inline: 'nearest'
              });
            }
          }
        }
        return;
      }

      if (typeof detail.imageIndex === 'number' && detail.imageIndex >= 0) {
        this.goTo(detail.imageIndex);
      }
    }

    _pauseVideoInSlide(index) {
      var slide = this.slides[index];
      if (!slide) {
        return;
      }
      var video = slide.querySelector('video');
      if (video && typeof video.pause === 'function' && !video.paused) {
        video.pause();
      }
    }

    _loadSlideImage(index) {
      var image = this.slides[index]?.querySelector('img[data-src]');
      if (!image) return;
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    }

    _setActiveIndex(index, options) {
      // Native lazy loading fetches many horizontal slides ahead. Fetch only
      // the selected slide and its neighbours, plus slides entering the viewport.
      [index - 1, index, index + 1].forEach((i) => this._loadSlideImage(i));
      var previousIndex = this.currentIndex;
      this.currentIndex = index;

      if (previousIndex !== index) {
        this._pauseVideoInSlide(previousIndex);
      }

      if (this.counterCurrent) {
        this.counterCurrent.textContent = String(index + 1);
      }

      this.thumbs.forEach((thumb) => {
        var thumbIndex = parseInt(thumb.getAttribute('data-index'), 10);
        var isActive = thumbIndex === index;
        thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (this.prevBtn) this.prevBtn.disabled = index === 0;
      if (this.nextBtn) this.nextBtn.disabled = index === this.slides.length - 1;

      var activeThumb = this.thumbs[index];
      if (activeThumb) {
        var strip = this.querySelector('[data-gtp-thumbs]');
        var thumbRect = activeThumb.getBoundingClientRect();
        var stripRect = strip.getBoundingClientRect();
        var offset = thumbRect.left < stripRect.left ? thumbRect.left - stripRect.left :
          thumbRect.right > stripRect.right ? thumbRect.right - stripRect.right : 0;
        if (offset) strip.scrollTo({ left: strip.scrollLeft + offset, behavior: 'instant' });
      }

      if (options && options.scroll === false) {
        return;
      }

      var targetSlide = this.slides[index];
      if (targetSlide) {
        var left = this.track.scrollLeft + targetSlide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollTo({ left: left, behavior: this.reducedMotion ? 'instant' : 'smooth' });
      }
    }

    goTo(index) {
      if (!this.slides || this.slides.length === 0) {
        return;
      }
      clearTimeout(this._scrollTimer);
      var clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      this._setActiveIndex(clamped, { scroll: true });
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    prev() {
      this.goTo(this.currentIndex - 1);
    }
  }

  customElements.define('gtp-gallery', GtpGallery);
}
